import { type FC, type ReactNode } from "react";
import { type GapKey, type Columns, gridContainer } from "./styles.css";

type Breakpoint = "medium" | "large";
type Responsive<T> = T | ({ base: T } & Partial<Record<Breakpoint, T>>);
type GridContainerSelection = NonNullable<Parameters<typeof gridContainer>[0]>;
type VariantSelection = Partial<GridContainerSelection>;

// columns、gapX、gapYには固定値のほか、レスポンシブ指定が可能
// 例：gapX: 12 | { base: 8, medium: 12, large: 16 }
// レスポンシブ指定する場合baseは必須、medium、largeは任意
type Props = {
  children: ReactNode;
  columns: Responsive<Columns>;
  gapX?: Responsive<GapKey>;
  gapY?: Responsive<GapKey>;
  className?: string;
};

// レスポンシブ指定かどうかを判定
const isResponsiveConfig = <T,>(
  value: Responsive<T>,
): value is { base: T } & Partial<Record<Breakpoint, T>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Propsからrecipe用のクラス名を構築
const buildVariants = (props: Pick<Props, "columns" | "gapX" | "gapY">): VariantSelection => {
  const variants: VariantSelection = {};

  if (isResponsiveConfig(props.columns)) {
    const columnsValue = props.columns;
    variants.columns = columnsValue.base;
    if (columnsValue.medium !== undefined) variants.columnsMedium = columnsValue.medium;
    if (columnsValue.large !== undefined) variants.columnsLarge = columnsValue.large;
  } else {
    variants.columns = props.columns;
  }

  if (props.gapX !== undefined) {
    if (isResponsiveConfig(props.gapX)) {
      const gapXValue = props.gapX;
      variants.gapX = gapXValue.base;
      if (gapXValue.medium !== undefined) variants.gapXMedium = gapXValue.medium;
      if (gapXValue.large !== undefined) variants.gapXLarge = gapXValue.large;
    } else {
      variants.gapX = props.gapX;
    }
  }

  if (props.gapY !== undefined) {
    if (isResponsiveConfig(props.gapY)) {
      const gapYValue = props.gapY;
      variants.gapY = gapYValue.base;
      if (gapYValue.medium !== undefined) variants.gapYMedium = gapYValue.medium;
      if (gapYValue.large !== undefined) variants.gapYLarge = gapYValue.large;
    } else {
      variants.gapY = props.gapY;
    }
  }

  return variants;
};

export const Grid: FC<Props> = ({ children, className, columns, gapX = 0, gapY = 0 }) => {
  const variantProps = buildVariants({ columns, gapX, gapY });
  const recipeClass = gridContainer(variantProps);

  return <div className={[recipeClass, className].filter(Boolean).join(" ")}>{children}</div>;
};
